import React, { useState, useEffect } from "react";
import { Tooltip } from "antd";
import { FileText, ChevronDown, ChevronUp, Trash2, Maximize2, ClipboardList, ExternalLink, Download } from "lucide-react";
import { ArtifactViewerPanel } from "./ArtifactViewerPanel";
import { renderMarkdown } from "utils/renderMarkdown";
import { exportTarPdf } from "utils/tarPdfExport";
import { useNavigate } from "react-router-dom";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface LinkedAAR {
  id: string;
  meeting_name: string;
  meeting_date: string;
  status: "open" | "closed";
  mode: "solo" | "group";
  created_at: string;
}

export interface SessionArtifact {
  id: string;
  title: string;
  type: "text_document";
  content: string;
  createdAt: string;
  source?: string;
  /** Used for visibility gating — which period type does this artifact belong to? */
  periodType?: "weekly" | "qbr" | "nqo";
  /** Used for per-period visibility gating — which specific period key? */
  periodKey?: string;
}

const ARTIFACT_TYPES = [
  { label: "Text Documents", active: true },
  { label: "Audio", active: false },
  { label: "Video", active: false },
  { label: "Slide Decks", active: false },
  { label: "Images", active: false },
  { label: "Visual / Graphic", active: false },
];

// renderMarkdown imported from utils/renderMarkdown

interface Props {
  artifacts: SessionArtifact[];
  sessionId?: string;
  companyName?: string;
  onArtifactClick?: (artifact: SessionArtifact) => void;
  onDeleteArtifact?: (artifactId: string) => void;
}

export const SessionStudioPanel: React.FC<Props> = ({ artifacts, sessionId, companyName, onDeleteArtifact }) => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [focusedArtifact, setFocusedArtifact] = useState<SessionArtifact | null>(null);
  const [linkedAARs, setLinkedAARs] = useState<LinkedAAR[]>([]);

  const textArtifacts = artifacts.filter((a) => a.type === "text_document");

  useEffect(() => {
    if (!sessionId) return;
    fetch(`${SERVER_URL}/api/aar/sessions/linked/${sessionId}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.sessions) setLinkedAARs(data.sessions); })
      .catch(() => {});
  }, [sessionId]);

  if (focusedArtifact) {
    return <ArtifactViewerPanel artifact={focusedArtifact} onBack={() => setFocusedArtifact(null)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="flex justify-between items-center">
          <span style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Studio</span>
          <span
            style={{
              fontSize: 11,
              color: "#64748b",
              background: "#f1f5f9",
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            {textArtifacts.length}
          </span>
        </div>
        <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0" }}>
          Artifacts from this session
        </p>
      </div>

      {/* Type filter */}
      <div className="px-4 pt-3 pb-2 flex flex-wrap gap-1.5">
        {ARTIFACT_TYPES.map((type) => (
          <Tooltip key={type.label} title={!type.active ? "Coming Soon" : undefined}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: type.active ? 500 : 400,
                border: `1px solid ${type.active ? "#2c1561" : "#e2e8f0"}`,
                background: type.active ? "#2c1561" : "#f8fafc",
                color: type.active ? "#fff" : "#94a3b8",
                cursor: type.active ? "pointer" : "not-allowed",
              }}
            >
              {type.label}
              {!type.active && (
                <span
                  style={{
                    fontSize: 9,
                    background: "#e2e8f0",
                    color: "#94a3b8",
                    borderRadius: 999,
                    padding: "1px 5px",
                  }}
                >
                  Soon
                </span>
              )}
            </span>
          </Tooltip>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px" }}>
        {/* Linked AARs */}
        {linkedAARs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
                paddingBottom: 6,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <ClipboardList style={{ width: 12, height: 12, color: "#94a3b8" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                After Action Reviews
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {linkedAARs.map((aar) => (
                <button
                  key={aar.id}
                  onClick={() => navigate(`/practices/aar?session=${aar.id}`)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    background: "#fafafa",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#c4b5d4")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                >
                  <ClipboardList style={{ width: 12, height: 12, color: "#2c1561", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {aar.meeting_name}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                      {new Date(aar.meeting_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      {" · "}
                      <span style={{ color: aar.status === "closed" ? "#22c55e" : "#f59e0b" }}>
                        {aar.status === "closed" ? "Closed" : "Open"}
                      </span>
                    </div>
                  </div>
                  <ExternalLink style={{ width: 11, height: 11, color: "#cbd5e1", flexShrink: 0 }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {textArtifacts.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <FileText style={{ width: 20, height: 20, color: "#94a3b8" }} />
            </div>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#475569", margin: "0 0 4px" }}>
              No artifacts yet
            </p>
            <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
              Run a team alignment synthesis from the Progress tab and it will appear here.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {textArtifacts.map((artifact) => {
              const isExpanded = expandedId === artifact.id;
              return (
                <div
                  key={artifact.id}
                  style={{
                    border: `1px solid ${isExpanded ? "#c4b5d4" : "#e2e8f0"}`,
                    borderRadius: 8,
                    background: isExpanded ? "#faf8ff" : "#fafafa",
                    overflow: "hidden",
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                >
                  {/* Card header — always visible */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : artifact.id)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      width: "100%",
                      padding: "10px 12px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <FileText
                      style={{ width: 14, height: 14, color: "#2c1561", flexShrink: 0, marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#0f172a",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {artifact.title}
                      </div>
                      {artifact.source && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94a3b8",
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {artifact.source}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      {artifact.content && (
                        <Tooltip title="Download as PDF">
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportTarPdf(artifact.content, {
                                companyName: companyName || "Roadmap Session",
                                periodLabel: artifact.title || "Team Alignment Report",
                                generatedAt: artifact.createdAt,
                              });
                            }}
                            style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#2c1561")}
                            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                          >
                            <Download style={{ width: 12, height: 12 }} />
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Focus mode">
                        <span
                          role="button"
                          onClick={(e) => { e.stopPropagation(); setFocusedArtifact(artifact); }}
                          style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#2c1561")}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                        >
                          <Maximize2 style={{ width: 12, height: 12 }} />
                        </span>
                      </Tooltip>
                      {onDeleteArtifact && (
                        <span
                          role="button"
                          onClick={(e) => { e.stopPropagation(); onDeleteArtifact(artifact.id); }}
                          style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#ef4444")}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                        >
                          <Trash2 style={{ width: 12, height: 12 }} />
                        </span>
                      )}
                      {isExpanded
                        ? <ChevronUp style={{ width: 13, height: 13, color: "#94a3b8" }} />
                        : <ChevronDown style={{ width: 13, height: 13, color: "#94a3b8" }} />
                      }
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div
                      style={{
                        borderTop: "1px solid #e2e8f0",
                        padding: "12px 14px 14px",
                        background: "#fff",
                      }}
                    >
                      {renderMarkdown(artifact.content, { context: "studio" })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
